import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Paper, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { RootState } from '../../store/store';
import {
  OwnershipFilter,
  ownershipOf,
  setOwnershipFilter,
} from '../../store/slices/dronesSlice';
import { DroneOwnership } from '../../types/drone';
import { usePreferenceWriter } from '../../hooks/useCurrentUser';

/**
 * "My drones" / "Others" / "Unclaimed" / "All" — the fleet view's ownership
 * filter (#385).
 *
 * Drones are owned by the operator who enrolled them (#381) and the fleet is
 * scoped to that owner server-side (#382), so the list an ordinary operator
 * receives is already only theirs plus the **unclaimed** ones: aircraft
 * registered through the factory provisioning flow, where no operator was
 * present to own them.
 *
 * An admin holds `fleet:read_all` and receives **everybody's**, which is why
 * there is a third bucket (#575). Until then "Mine" meant "has an owner" and
 * an admin was told that other people's aircraft were theirs — in a system
 * where the next click arms an aircraft, that is a safety-relevant falsehood
 * and not a cosmetic one.
 *
 * This makes that distinction visible and selectable, and **remembers the
 * choice**: it is saved to the operator's preferences (#377) and hydrated on
 * load, so "show only mine" stays selected across reloads and devices.
 *
 * A *first* visit opens on All rather than on Mine — an unclaimed aircraft can
 * be airborne, and a view that hides it is one nobody is watching. That is a
 * starting point, not a policy: after the first click, the operator's own choice
 * is the default.
 *
 * The counts are part of the point — "Unclaimed 3" is how an operator finds out
 * there are three aircraft nobody has claimed, which is the prompt to claim
 * them.
 */
export const FleetOwnershipFilter: React.FC = () => {
  const dispatch = useDispatch();
  const drones = useSelector((state: RootState) => state.drones.drones);
  const filter = useSelector((state: RootState) => state.drones.ownershipFilter);
  const { loaded, savePreference } = usePreferenceWriter();

  // Counted per bucket rather than by subtraction (#575). `mine = all -
  // unclaimed` was arithmetic standing in for a question nobody could answer
  // here: for an admin, "not unclaimed" includes every other operator's
  // aircraft, and the control confidently reported them as theirs.
  const counts = useMemo(() => {
    const all = Object.values(drones);
    const of = (kind: DroneOwnership) =>
      all.filter((drone) => ownershipOf(drone) === kind).length;
    return {
      all: all.length,
      mine: of('mine'),
      others: of('others'),
      unclaimed: of('unclaimed'),
    };
  }, [drones]);

  // Nothing to filter. An empty control is a widget explaining a distinction
  // the operator cannot see an instance of.
  if (counts.all === 0) {
    return null;
  }

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: OwnershipFilter | null
  ) => {
    // ToggleButtonGroup emits null when the active button is clicked again.
    // Keeping the current filter is the right answer: deselecting everything
    // would show an empty map with no control in the pressed state to explain
    // why.
    if (next === null) {
      return;
    }

    // The view changes immediately; persisting it is a background courtesy.
    dispatch(setOwnershipFilter(next));

    // Only once the stored document has actually arrived. Saving before then
    // would write the *defaults* for everything else in it — silently resetting
    // the operator's theme and planner settings to make a map filter stick.
    if (loaded) {
      // savePreference, not savePreferences: spreading the stored document
      // here wrote the server's theme back over the one the operator had just
      // picked with the header toggle (#528).
      void savePreference({ fleet: { ownership: next } });
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 0.5 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={handleChange}
        aria-label="Which drones to show"
      >
        <ToggleButton value="all" aria-label={`All drones, ${counts.all}`}>
          All {counts.all}
        </ToggleButton>
        <ToggleButton value="mine" aria-label={`My drones, ${counts.mine}`}>
          Mine {counts.mine}
        </ToggleButton>
        {/* Only for a caller who can see another operator's fleet (#575).
            An ordinary operator's response never contains one, and a chip
            reading "Others 0" would explain a distinction they cannot have an
            instance of. */}
        {counts.others > 0 && (
          <Tooltip title="Drones owned by another operator. You can see them because you hold fleet-wide read access — they are not yours, and somebody else is accountable for them.">
            <ToggleButton
              value="others"
              aria-label={`Other operators' drones, ${counts.others}`}
            >
              Others {counts.others}
            </ToggleButton>
          </Tooltip>
        )}
        <Tooltip title="Drones with no owner — registered without an operator signed in. Any operator can see and command them.">
          <ToggleButton
            value="unclaimed"
            aria-label={`Unclaimed drones, ${counts.unclaimed}`}
          >
            Unclaimed {counts.unclaimed}
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Paper>
  );
};
